import React, { forwardRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  href?: string;
}
// Using motion.button for built-in animation capabilities
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant = 'primary',
  size = 'md',
  fullWidth,
  href,
  children,
  ...props
}, ref) => {
  const variants = {
    primary: 'bg-[#9AC1F0] text-white hover:bg-[#7BA8D8] shadow-lg shadow-blue-400/20',
    secondary: 'bg-[#FFD700] text-[#9AC1F0] hover:bg-[#fcc419] font-bold shadow-md',
    outline: 'border-2 border-[#9AC1F0] text-[#9AC1F0] hover:bg-blue-50',
    danger: 'bg-red-600 text-white hover:bg-red-700'
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl font-bold uppercase tracking-wide'
  };
  const baseClasses = cn('inline-flex items-center justify-center rounded-full font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none', variants[variant], sizes[size], fullWidth ? 'w-full' : '', className);
  if (href) {
    return <motion.a href={href} whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} className={baseClasses} target="_blank" rel="noopener noreferrer" data-id="element-109">
          {children}
        </motion.a>;
  }
  return <motion.button ref={ref} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} className={baseClasses} {...props as any} data-id="element-110">
        {children}
      </motion.button>;
});
Button.displayName = 'Button';